import { renderFrameCanvas } from "./framePdf";

import type { ExcalidrawFrameLikeElement } from "@excalidraw/excalidraw/element/types";
import type { FramePdfSource } from "./framePdf";

/** The server takes at most 4MB and 2400px per side. */
export const GALLERY_MAX_BYTES = 4 * 1024 * 1024;
/** Longest side tried first, then smaller ones until the file fits. */
export const GALLERY_LONG_SIDES = [1680, 1260, 960, 720] as const;
const WEBP_QUALITY = 0.85;

const toBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

/** WebP when the browser can make one; a browser that can't answers an
 * unknown type with null or a PNG, so PNG is asked for explicitly then. */
const encode = async (canvas: HTMLCanvasElement): Promise<Blob | null> => {
  const webp = await toBlob(canvas, "image/webp", WEBP_QUALITY);
  if (webp && webp.type === "image/webp") {
    return webp;
  }
  const png = await toBlob(canvas, "image/png");
  return png && png.type === "image/png" ? png : null;
};

/**
 * Renders one frame the way the PDF export does and encodes it for the
 * gallery. A drawing that stays over 4MB is re-rendered smaller rather than
 * rejected by the server.
 */
export const encodeFrameForGallery = async (
  source: FramePdfSource,
  frame: ExcalidrawFrameLikeElement,
): Promise<Blob> => {
  for (const longSide of GALLERY_LONG_SIDES) {
    const canvas = await renderFrameCanvas(source, frame, longSide);
    const blob = await encode(canvas);
    if (!blob) {
      throw new Error("Could not encode the frame image");
    }
    if (blob.size <= GALLERY_MAX_BYTES) {
      return blob;
    }
  }
  throw new Error("Frame image is too large even at the smallest size");
};
